import React, {
  ReactNode,
  Ref,
  useCallback,
  useRef,
  InputHTMLAttributes,
} from "react";

import classnames from "classnames";

import { StyledInput, StyledInputWrapper } from "./Styles";
import { InputMode, InputSize } from "./types";
import useForkRef from "../../utils/useForkRef";
import { ITypographyProps } from "../Typography/Typography";

export interface OverrideProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "ref"> {
  /** Velicina inputa koja definse font-size i border radius */
  size?: InputSize;
}

export interface InputProps
  extends OverrideProps,
    Pick<ITypographyProps, "align"> {
  invalid?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  wrapRef?: Ref<HTMLDivElement>;
  mode?: InputMode;
  wrapperClick?: () => void;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = "",
      style,
      type = "text",
      disabled = false,
      size = "regular",
      invalid = false,
      startAdornment,
      endAdornment,
      wrapRef,
      wrapperClick,
      mode = "outlined",
      ...rest
    },
    ref
  ) => {
    const intInputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef(ref, intInputRef);

    const handleWrapperClick = useCallback(() => {
      intInputRef.current?.focus();
      wrapperClick && wrapperClick();
    }, [wrapperClick]);

    return (
      <StyledInputWrapper
        $size={size}
        $invalid={invalid}
        $disabled={disabled}
        $mode={mode}
        style={style}
        className={classnames("c-input-wrapper", className)}
        onClick={handleWrapperClick}
        ref={wrapRef}
      >
        {startAdornment}
        <StyledInput
          ref={handleRef}
          type={type}
          $size={size}
          disabled={disabled}
          className="c-input"
          aria-invalid={invalid}
          $invalid={invalid}
          $mode={mode}
          data-form-type="other"
          {...rest}
        />
        {endAdornment}
      </StyledInputWrapper>
    );
  }
);

Input.displayName = "Input";
